import React from "react";
import { useSaleExpiration, useSubscriptions } from "./subscriptionHooks";
import { useStoreIds } from "./subscriptionIds";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { format } from "date-fns";
import { SALE } from "sharedJs__generated/constants";
import Snowfall from "react-snowfall";

// Constants
const SECONDS_PER_DAY = 24 * 60 * 60;

const Countdown = styled.div`
  position: absolute;
  z-index: 10;
  bottom: -12px;
  left: -2px;
  right: -2px;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.duo.color.green};
  color: #fff;
  font-size: 12px;
  line-height: 1.6em;
  font-weight: 400;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 5px;
`;

const BlackFriday = styled(Countdown)`
  background: ${({ theme }) => theme.duo.color.black};
  color: ${({ theme }) => theme.duo.color.white};
  text-transform: uppercase;
  font-weight: 700;
  min-width: 150%;
  margin-left: -25%;
`;
const ChristmasFamily = styled(Countdown)`
  bottom: 0;
  transform: translateY(calc(100% - 4px));
  margin: 0 -12px;
  padding: 8px;
  background: green;
  overflow: visible;
  white-space: wrap;
  line-height: 1.3em;
`;
const MotherLangDay = styled(Countdown)`
  bottom: -30px;
  overflow: visible;
  padding: 5px;
  margin: 0 -25px;
  transform: translateY(calc(100% - 47px));
  white-space: wrap;
  background: ${({ theme }) => theme.duo.color.primary};
`;
const Summer = styled(Countdown)`
  bottom: -30px;
  overflow: visible;
  padding: 5px;
  margin: 0 -15px;
  transform: translateY(calc(100% - 47px));
  white-space: wrap;
  background: ${({ theme }) => theme.duo.color.themeGradient};
`;
const Present = styled.div`
  position: absolute;
  right: -3px;
  bottom: -3px;
  font-size: 28px;
`;

const PriceSale = () => {
  const { saleExpiration, saleDiscount, saleActive, saleId } = useSaleExpiration();
  const { subscriptions } = useSubscriptions();
  const { storeIdYear } = useStoreIds();
  const [secondsLeft, setSecondsLeft] = React.useState(saleExpiration);
  const { t } = useTranslation();

  React.useEffect(() => {
    setSecondsLeft(saleExpiration);
    const interval = setInterval(() => setSecondsLeft((val) => (val > 0 ? val - 1 : 0)), 1000);
    return () => clearInterval(interval);
  }, [saleExpiration]);

  // Helper functions
  const formatTimeLeft = (time: number) => format(new Date(time * 1000), "HH:mm:ss");

  const renderDiscountInfo = () => {
    const timeDisplay = secondsLeft < SECONDS_PER_DAY ? ` | ${formatTimeLeft(secondsLeft)}` : "";
    return `-${saleDiscount}%${timeDisplay}`;
  };

  // Early returns
  if (!subscriptions) return null;
  if (!saleActive && !subscriptions[storeIdYear]?.isTrial) return null;

  // Sale-specific rendering
  if (saleId === SALE.christmasFamily) {
    return (
      <ChristmasFamily>
        <Snowfall snowflakeCount={4} speed={[0.05, 0.05]} />
        <Trans>Same price as the individual subscription</Trans>
        <Present>🎁</Present>
      </ChristmasFamily>
    );
  }

  if (saleId === SALE.blackFriday) {
    return (
      <BlackFriday>
        <Trans>Expires soon!</Trans>
      </BlackFriday>
    );
  }

  if (saleId === SALE.motherLangDay) {
    return (
      <MotherLangDay>
        {renderDiscountInfo()}
        <br />
        <strong>
          <Trans>Mother Language Day</Trans>
        </strong>
      </MotherLangDay>
    );
  }

  if (saleId === SALE.summer) {
    return (
      <Summer>
        {renderDiscountInfo()}
        <br />
        <strong>
          <Trans>Summer Sale</Trans>
        </strong>
      </Summer>
    );
  }

  // Default sale display
  return (
    <Countdown>
      {!subscriptions[storeIdYear]?.isTrial
        ? `-${saleDiscount}% | ${secondsLeft ? formatTimeLeft(secondsLeft) : t("season")}`
        : "7 days free"}
    </Countdown>
  );
};

export default PriceSale;
